import {
  Badge,
  BadgeVariantEnum,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  NewsMediaIcon,
  Tooltip,
  Typography,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Listbox from '@/components/Listbox';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { ChannelType } from '@/features/broadcasters/types';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { TFilterProps } from '@/types';

import { TRangeActiveOptions } from '../active-options/active-options';
import {
  CatalogueActiveOptions,
  TUseCatalogueOptions,
} from '../active-options/CatalogueActiveOptions';

const toChannelItem = (ch: ChannelType) => ({
  value: ch.id,
  label: ch.short_name ? `${ch.name} (${ch.short_name})` : ch.name,
});

const useChannelOptions: TUseCatalogueOptions<ChannelType> = (request, config) =>
  useChannels({
    params:
      'range' in request ? { isParentChannel: false, ...request.range } : { idIn: request.idIn },
    config,
  });

export function MentionFilterBroadcast({
  isOpen,
  isDark,
  showLabel,
  onToggleFilter,
  onClose,
  activeOptions,
}: TFilterProps & { activeOptions?: TRangeActiveOptions }) {
  const { t } = useTranslation();
  const { broadcasters: state, setFilters: setState } = useProgramBroadcastFilters();
  const { data: { data: channelsData = [] } = {} } = useChannels({
    params: { isParentChannel: false },
    config: { enabled: !activeOptions },
  });

  const allChannels = useMemo(() => channelsData.map(toChannelItem), [channelsData]);

  const handleChangeState = (value: string[]) => {
    setState({ broadcasters: value });
  };

  const ToolTipWrapper = ({ children }: { children: React.ReactNode }) => {
    if (showLabel) return <>{children}</>;
    return (
      <Tooltip help={t('Emisoras')} grow>
        {children}
      </Tooltip>
    );
  };

  return (
    <div className="relative w-full md:w-auto">
      <div
        onClick={onToggleFilter}
        className={clsx(
          'group flex w-full cursor-pointer items-center border-b-[1px] border-stroke-30-1 p-1',
          { 'border-text-medium-20': isOpen && allChannels.length !== 0 && !isDark },
          { 'hover:border-text-medium-20': !isDark },
          { 'border-text-neutral-10 hover:bg-reverse-opacity-05/10': isDark },
          { 'bg-reverse-opacity-05/10': isOpen && isDark }
        )}
      >
        <ToolTipWrapper>
          <NewsMediaIcon
            className={clsx(
              'mr-1',
              {
                'text-text-medium-10': !isOpen && !isDark,
                'text-accent-active-primary-40': isOpen && !isDark,
              },
              {
                'text-text-neutral-10': isDark,
              },
              {
                'group-hover:text-accent-active-primary-40': !isDark,
              }
            )}
          />
        </ToolTipWrapper>
        {showLabel && (
          <Typography
            fontSize={FontSizeEnum.Sm}
            weight={FontWeightEnum.Medium}
            color={isDark ? FontColorEnum.Neutral : FontColorEnum.Medium}
            className={clsx(
              'select-none pr-size-4',
              {
                'text-text-medium-20': isOpen && !isDark,
              },
              {
                'group-hover:text-text-medium-20': !isDark,
              }
            )}
          >
            {t('Emisoras')}
          </Typography>
        )}
        {state.length > 0 && (
          <Badge
            variant={!isDark ? BadgeVariantEnum.DefaultFilled : BadgeVariantEnum.NeutralFilled}
            className="py-3"
          >
            + {state.length}
          </Badge>
        )}
      </div>
      {isOpen && (
        <div className="left-0 top-[120%] z-50 w-full bg-neutral-10 p-3 md:absolute md:min-w-[420px] md:shadow-full-medium">
          {activeOptions ? (
            <CatalogueActiveOptions
              activeOptions={activeOptions}
              useOptions={useChannelOptions}
              toItem={toChannelItem}
              isDark={isDark}
              selected={state}
              onChange={handleChangeState}
              onClose={onClose}
            />
          ) : (
            <Listbox
              dark={isDark}
              mode="multiple"
              items={allChannels}
              onChange={value => {
                handleChangeState(value);
              }}
              identifiers={{
                key: 'value',
                value: 'value',
                label: 'label',
                children: 'options',
              }}
              selected={state}
              onClose={onClose}
            />
          )}
        </div>
      )}
    </div>
  );
}
